"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Home, ArrowLeft, Mail } from "lucide-react";

export default function NotFound() {
  const [mounted, setMounted] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setMounted(true);
    const checkDark = () =>
      setIsDark(document.documentElement.classList.contains("dark"));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const svgAccent = isDark ? "#f5f4f5" : "#202e54";

  if (!mounted) return null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(135deg, #0b1120 0%, #0f1a2e 50%, #0b1120 100%)"
          : "linear-gradient(135deg, #eef3fb 0%, #f5f8fc 50%, #eaeff8 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Archivo', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── Background decorative shapes ──────────────────────────────────── */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Large diamond top-right */}
        <svg
          viewBox="0 0 200 200"
          style={{
            position: "absolute",
            top: "-60px",
            right: "-60px",
            width: "340px",
            opacity: isDark ? 0.04 : 0.06,
          }}
        >
          <rect
            fill="hsl(var(--primary))"
            x="50" y="50" width="100" height="100"
            transform="rotate(45 100 100)"
          />
        </svg>

        {/* Medium diamond bottom-left */}
        <svg
          viewBox="0 0 200 200"
          style={{
            position: "absolute",
            bottom: "-40px",
            left: "-40px",
            width: "260px",
            opacity: isDark ? 0.04 : 0.05,
          }}
        >
          <rect
            fill="hsl(var(--primary))"
            x="50" y="50" width="100" height="100"
            transform="rotate(45 100 100)"
          />
        </svg>

        {/* Small diamond mid-left */}
        <svg
          viewBox="0 0 100 100"
          style={{
            position: "absolute",
            top: "30%",
            left: "6%",
            width: "80px",
            opacity: isDark ? 0.06 : 0.07,
          }}
        >
          <rect
            fill="hsl(var(--primary))"
            x="25" y="25" width="50" height="50"
            transform="rotate(45 50 50)"
          />
        </svg>

        {/* Small diamond mid-right */}
        <svg
          viewBox="0 0 100 100"
          style={{
            position: "absolute",
            top: "55%",
            right: "8%",
            width: "60px",
            opacity: isDark ? 0.05 : 0.06,
          }}
        >
          <rect
            fill="hsl(var(--primary))"
            x="25" y="25" width="50" height="50"
            transform="rotate(45 50 50)"
          />
        </svg>

        {/* Horizontal line accent */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: "1px",
            background: isDark
              ? "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.15), transparent)"
              : "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.12), transparent)",
          }}
        />
      </div>

      {/* ── Main card ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: "520px",
          padding: "0 24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Logo */}
        <div style={{ marginBottom: "40px" }}>
          <div
            style={{
              width: "56px",
              height: "28px",
              margin: "0 auto 12px",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 98.37 49.69"
              style={{ width: "100%", height: "100%" }}
            >
              <rect fill={svgAccent} x="41.3" y="3.27" width="15.77" height="15.77"
                transform="translate(22.29 -31.51) rotate(45)" />
              <rect fill={svgAccent} x="41.3" y="30.64" width="15.77" height="15.77"
                transform="translate(41.65 -23.5) rotate(45)" />
              <rect fill="hsl(var(--primary))" x="54.99" y="16.95" width="15.77" height="15.77"
                transform="translate(35.98 -37.18) rotate(45)" />
              <polygon fill="hsl(var(--primary))"
                points="76.05 22.32 64.9 11.16 76.06 0 98.37 0 76.05 22.32" />
              <polygon fill={svgAccent}
                points="22.32 22.32 33.47 11.16 22.31 0 0 0 22.32 22.32" />
              <polygon fill="hsl(var(--primary))"
                points="22.32 27.37 33.47 38.52 22.31 49.69 0 49.69 22.32 27.37" />
              <polygon fill={svgAccent}
                points="76.05 27.37 64.9 38.52 76.06 49.69 98.37 49.69 76.05 27.37" />
              <rect fill="#2c5da8" x="27.61" y="16.95" width="15.77" height="15.77"
                transform="translate(27.96 -17.83) rotate(45)" />
            </svg>
          </div>
          <span
            style={{
              fontSize: "15px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              color: isDark ? "#e2eaf6" : "#202e54",
            }}
          >
            ZUNOX
          </span>
        </div>

        {/* 404 number */}
        <div style={{ position: "relative", marginBottom: "8px" }}>
          <span
            style={{
              fontSize: "clamp(96px, 20vw, 144px)",
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: "-0.04em",
              background: isDark
                ? "linear-gradient(135deg, hsl(var(--primary)) 0%, #4a7fd4 50%, hsl(var(--primary)) 100%)"
                : "linear-gradient(135deg, #1a3d7a 0%, hsl(var(--primary)) 50%, #1a3d7a 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              display: "block",
              userSelect: "none",
            }}
          >
            404
          </span>
          {/* Glow under the 404 */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "60%",
              height: "24px",
              background: "radial-gradient(ellipse, hsl(var(--primary) / 0.25) 0%, transparent 70%)",
              filter: "blur(6px)",
            }}
          />
        </div>

        {/* Divider */}
        <div
          style={{
            width: "48px",
            height: "3px",
            borderRadius: "2px",
            background: "linear-gradient(90deg, hsl(var(--primary)), #4a7fd4)",
            margin: "24px auto",
          }}
        />

        {/* Title */}
        <h1
          style={{
            fontSize: "clamp(20px, 4vw, 26px)",
            fontWeight: 700,
            color: isDark ? "#e2eaf6" : "#202e54",
            margin: "0 0 12px",
            lineHeight: 1.3,
          }}
        >
          Página não encontrada
        </h1>

        {/* Description */}
        <p
          style={{
            fontSize: "15px",
            lineHeight: 1.7,
            color: isDark ? "#5a7a9a" : "#6b7f9a",
            margin: "0 0 40px",
            maxWidth: "380px",
          }}
        >
          O endereço que tentou aceder não existe ou foi movido.
          Verifique o URL ou regresse à página inicial.
        </p>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {/* Primary — Go home */}
          <Link
            href="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "11px 24px",
              borderRadius: "6px",
              background: "hsl(var(--primary))",
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: 600,
              textDecoration: "none",
              letterSpacing: "0.01em",
              boxShadow: isDark
                ? "0 4px 16px hsl(var(--primary) / 0.35)"
                : "0 4px 16px hsl(var(--primary) / 0.25)",
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = "#1e4a8c";
              (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = isDark
                ? "0 6px 20px hsl(var(--primary) / 0.45)"
                : "0 6px 20px hsl(var(--primary) / 0.35)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = "hsl(var(--primary))";
              (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)";
              (e.currentTarget as HTMLAnchorElement).style.boxShadow = isDark
                ? "0 4px 16px hsl(var(--primary) / 0.35)"
                : "0 4px 16px hsl(var(--primary) / 0.25)";
            }}
          >
            <Home size={15} strokeWidth={2.2} />
            Página inicial
          </Link>

          {/* Secondary — Go back */}
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = "/")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "11px 24px",
              borderRadius: "6px",
              background: "transparent",
              border: `1.5px solid ${isDark ? "hsl(var(--primary) / 0.45)" : "hsl(var(--primary) / 0.3)"}`,
              color: isDark ? "#7aa4d4" : "hsl(var(--primary))",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              letterSpacing: "0.01em",
              transition: "all 0.18s ease",
              fontFamily: "'Archivo', sans-serif",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = isDark
                ? "hsl(var(--primary) / 0.12)"
                : "hsl(var(--primary) / 0.06)";
              (e.currentTarget as HTMLButtonElement).style.borderColor = "hsl(var(--primary))";
              (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.borderColor = isDark
                ? "hsl(var(--primary) / 0.45)"
                : "hsl(var(--primary) / 0.3)";
              (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
            }}
          >
            <ArrowLeft size={15} strokeWidth={2.2} />
            Voltar atrás
          </button>
        </div>

        {/* Divider */}
        <div
          style={{
            width: "100%",
            maxWidth: "360px",
            height: "1px",
            background: isDark
              ? "hsl(var(--primary) / 0.12)"
              : "hsl(var(--primary) / 0.1)",
            margin: "48px auto 32px",
          }}
        />

        {/* Support note */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: isDark ? "#3d5a78" : "#8ba0ba",
            fontSize: "13px",
          }}
        >
          <Mail size={13} strokeWidth={1.8} />
          <span>
            Precisa de ajuda?{" "}
            <a
              href="mailto:support@zunox.pt"
              style={{
                color: isDark ? "#4a7fd4" : "hsl(var(--primary))",
                textDecoration: "none",
                fontWeight: 500,
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLAnchorElement).style.textDecoration = "none")
              }
            >
              support@zunox.pt
            </a>
          </span>
        </div>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          bottom: "24px",
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: "12px",
          color: isDark ? "#2a3d52" : "#b0bfcc",
          letterSpacing: "0.02em",
        }}
      >
        © {new Date().getFullYear()} Zunox · Where your business runs
      </div>
    </div>
  );
}
